import { FormEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { answerHermes } from "../../../shared/guideChat";
import type { ChatTurn } from "../../../shared/openAsk";
import { localKv } from "../lib/kv";
import { HERMES_ASSISTANT_KEY } from "../lib/assistant-chrome";
import { useAssistantChrome } from "../hooks/use-assistant-chrome";

export function GuideAssistant() {
  const location = useLocation();
  const { enabled, minimized, setMinimized, fabPointer, consumeSwipe } = useAssistantChrome(HERMES_ASSISTANT_KEY);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [lines, setLines] = useState<ChatTurn[]>([
    { role: "assistant", text: "Asistente de Hermes. Pregúntame qué hacer, dónde ir, qué dibujar o cómo usar Planes." },
  ]);
  const [mounted, setMounted] = useState(false);
  const [pending, setPending] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight + 2}px`;
  }, [text, open]);
  const [authed, setAuthed] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    void localKv.get("hermes.session").then((raw) => setAuthed(Boolean(raw)));
    const id = window.setInterval(() => {
      void localKv.get("hermes.session").then((raw) => setAuthed(Boolean(raw)));
    }, 1500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!authed || !enabled || location.pathname !== "/") setOpen(false);
  }, [authed, enabled, location.pathname]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [lines, pending, open]);

  function send(event?: FormEvent) {
    event?.preventDefault();
    const next = text.trim();
    if (!next || pending) return;
    setText("");
    setPending(true);
    const withUser: ChatTurn[] = [...lines, { role: "user", text: next }];
    setLines(withUser);
    void answerHermes(next, undefined, withUser)
      .catch(() => "No he podido responder ahora. Inténtalo de nuevo.")
      .then((reply) => {
        setLines((later) => [...later, { role: "assistant", text: reply }]);
        setPending(false);
      });
  }

  if (!mounted || !authed || !enabled || location.pathname !== "/") return null;

  return createPortal(
    <div className={`assistant-dock${minimized ? " assistant-dock--minimized" : ""}`} data-assistant="hermes">
      {minimized ? (
        <button
          aria-label="Mostrar asistente"
          className="assistant-peep"
          onClick={() => setMinimized(false)}
          title="Asistente"
          type="button"
        >
          <span className="assistant-peep-dot" />
        </button>
      ) : (
        <>
          {open ? (
            <section className="assistant-panel" aria-label="Chat del asistente">
              <header className="assistant-header">
                <strong>Asistente</strong>
                <button type="button" className="assistant-close" onClick={() => setOpen(false)}>
                  Cerrar
                </button>
              </header>
              <div className="assistant-log" ref={logRef}>
                {lines.map((line, index) => (
                  <p key={index} className={`assistant-msg assistant-msg--${line.role}`}>
                    {line.text}
                  </p>
                ))}
                {pending ? <p className="assistant-msg assistant-msg--assistant assistant-msg--pending">Esperando respuesta...</p> : null}
              </div>
              <form className="assistant-form" onSubmit={send}>
                <textarea
                  ref={inputRef}
                  className="assistant-input"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Pregunta"
                  aria-label="Pregunta al asistente"
                  rows={2}
                />
                <button className="assistant-send" disabled={pending || !text.trim()} type="submit">
                  Enviar
                </button>
              </form>
            </section>
          ) : null}
          <button
            className="assistant-fab"
            type="button"
            onClick={() => {
              if (consumeSwipe()) return;
              setOpen((value) => !value);
            }}
            onPointerDown={fabPointer.onPointerDown}
            onPointerMove={fabPointer.onPointerMove}
            onPointerUp={fabPointer.onPointerUp}
            onPointerCancel={fabPointer.onPointerCancel}
            aria-label="Abrir asistente de Hermes"
            title="Desliza horizontalmente para minimizar"
          >
            Asistente
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
